import { useEffect, useMemo, useRef, useState } from 'react';
import { useFirebase } from './FirebaseContext';
import { createDirectoryService } from '../directory/mentorDirectory.mjs';

export function useDirectoryService() {
  const { auth, firestore, user } = useFirebase();
  return useMemo(() => !user || user.isAnonymous ? null : createDirectoryService({ auth, firestore, account: user }), [auth, firestore, user]);
}

// Synchronously suppress a previous account/query's data before subscription effects run.
function useSubscription(key, subscribe, dependencies) {
  const [state, setState] = useState({ key: null, status: 'loading', data: null, error: null });
  const [retry, setRetry] = useState(0);
  const activeKey = useRef(key);
  activeKey.current = key;
  useEffect(() => {
    let active = true;
    setState({ key, status: 'loading', data: null, error: null });
    if (!subscribe) return () => { active = false; };
    const current = () => active && activeKey.current === key;
    let stop;
    try {
      stop = subscribe(data => { if (current()) setState({ key, status: 'ready', data, error: null }); },
        error => { if (current()) setState({ key, status: 'error', data: null, error }); });
    } catch (error) { if (current()) setState({ key, status: 'error', data: null, error }); }
    return () => { active = false; stop?.(); };
    // The caller supplies the stable query inputs; callback identities are intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, retry, ...dependencies]);
  return { ...(state.key === key ? state : { status: 'loading', data: null, error: null }), retry: () => setRetry(value => value + 1) };
}

export function useDirectoryPage(filters, cursor) {
  const { user } = useFirebase();
  const service = useDirectoryService();
  const key = JSON.stringify([user?.uid, filters.name, filters.state, filters.professionalField, cursor?.id, cursor?.data()?.nameKey]);
  return useSubscription(key, service ? (next, error) => service.subscribePage(filters, cursor, next, error) : null, [service]);
}

export function useDirectoryListing(uid) {
  const { user } = useFirebase();
  const service = useDirectoryService();
  const key = JSON.stringify([user?.uid, uid]);
  return useSubscription(key, service && uid ? (next, error) => service.subscribeListing(uid, next, error) : null, [service]);
}
